import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Collapse,
  Divider,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Publish as ReleaseIcon,
  Delete as DiscardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import ChainOfThought from '../components/ChainOfThought';
import api from '../api/axios';
import { useNavigate, useParams } from 'react-router-dom';
import type { TestRun, PromptVersion, TestExtractionRun } from '../types/prompts';

/** Format milliseconds as seconds (e.g., 3200 → "3.2s") */
function formatLatency(ms: number | null): string {
  if (ms === null || ms === undefined) return '-';
  const seconds = ms / 1000;
  return `${seconds.toFixed(1)}s`;
}

/** Format token count with 'k' for thousands (e.g., 14500 → "14.5k") */
function formatTokens(count: number | null | undefined): string {
  if (count === null || count === undefined) return '-';
  if (count >= 1000) {
    const k = count / 1000;
    return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`;
  }
  return count.toString();
}

export default function PromptTestRunner() {
  const navigate = useNavigate();
  const { versionId } = useParams<{ versionId: string }>();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // State
  const [version, setVersion] = useState<PromptVersion | null>(null);
  const [testRun, setTestRun] = useState<TestRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedExtractions, setExpandedExtractions] = useState<string[]>([]);

  // Load prompt version
  useEffect(() => {
    if (!versionId) return;

    api.get<PromptVersion>(`/schemes/prompts/versions/${versionId}/`)
      .then((res) => {
        setVersion(res.data);
        // Check for existing test run
        if (res.data.latest_test_run) {
          loadTestRun(res.data.latest_test_run.id);
        }
      })
      .catch((err) => {
        console.error('Failed to load version:', err);
        setError('Failed to load prompt version');
      })
      .finally(() => setLoading(false));

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [versionId]);

  const loadTestRun = async (testRunId: string) => {
    try {
      const res = await api.get<TestRun>(`/schemes/prompts/test-runs/${testRunId}/`);
      setTestRun(res.data);

      // Start polling if test is running
      if (res.data.status === 'pending' || res.data.status === 'running') {
        startPolling(testRunId);
      }
    } catch (err) {
      console.error('Failed to load test run:', err);
    }
  };

  const startPolling = (testRunId: string) => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await api.get<TestRun>(`/schemes/prompts/test-runs/${testRunId}/`);
        setTestRun(res.data);

        // Stop polling when complete
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleStartTest = async () => {
    if (!versionId) return;

    setStarting(true);
    setError(null);

    try {
      const res = await api.post<TestRun>(`/schemes/prompts/versions/${versionId}/test/`);
      setTestRun(res.data);
      startPolling(res.data.id);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to start test');
    } finally {
      setStarting(false);
    }
  };

  const handleRelease = async () => {
    if (!versionId) return;

    try {
      await api.post(`/schemes/prompts/versions/${versionId}/release/`);
      navigate('/schemes/evaluate?tab=entity-types');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to release');
    }
  };

  const handleDiscard = async () => {
    if (!versionId) return;

    if (!window.confirm('Are you sure you want to discard this draft?')) {
      return;
    }

    try {
      await api.delete(`/schemes/prompts/versions/${versionId}/`);
      navigate('/schemes/evaluate?tab=entity-types');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to discard');
    }
  };

  const toggleExtraction = (extractionId: string) => {
    setExpandedExtractions(prev =>
      prev.includes(extractionId)
        ? prev.filter(id => id !== extractionId)
        : [...prev, extractionId]
    );
  };

  const formatOutput = (output: unknown): string => {
    if (!output) return '-';
    if (typeof output === 'string') return output;
    try {
      return JSON.stringify(output, null, 2);
    } catch {
      return String(output);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <SuccessIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <PendingIcon color="disabled" fontSize="small" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const isRunning = testRun?.status === 'pending' || testRun?.status === 'running';
  const isComplete = testRun?.status === 'completed';
  const isFailed = testRun?.status === 'failed';
  const canStartNewTest = !testRun || isFailed;

  return (
    <Layout>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/schemes/prompts/editor/${versionId}`)} size="small">
              <BackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              Test Results: {version?.entity_type_name || ''}
            </Typography>
            {version && (
              <Chip label={version.version} size="small" variant="outlined" />
            )}
            {testRun && (
              <Chip
                label={testRun.status}
                size="small"
                color={getStatusColor(testRun.status)}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canStartNewTest && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartTest}
                disabled={starting}
                startIcon={starting ? <CircularProgress size={16} /> : undefined}
              >
                {starting ? 'Starting...' : (isFailed ? 'Retry Test' : 'Start Test')}
              </Button>
            )}
            {isRunning && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={async () => {
                    if (!testRun) return;
                    if (!window.confirm('Are you sure you want to cancel this test run?')) return;
                    try {
                      await api.post(`/schemes/prompts/test-runs/${testRun.id}/cancel/`);
                      // Clear polling
                      if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                      }
                      // Reset state to allow starting a new test
                      setTestRun(null);
                    } catch (err: unknown) {
                      const error = err as { response?: { data?: { error?: string } } };
                      setError(error.response?.data?.error || 'Failed to cancel test');
                    }
                  }}
                >
                  Cancel Test
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => testRun && loadTestRun(testRun.id)}
                >
                  Refresh
                </Button>
              </>
            )}
            {(isComplete || testRun?.status === 'cancelled') && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleStartTest}
                  disabled={starting}
                  startIcon={starting ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {starting ? 'Starting...' : 'Rerun Test'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setTestRun(null)}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DiscardIcon />}
                  onClick={handleDiscard}
                >
                  Discard Draft
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/schemes/prompts/editor/${versionId}`)}
                >
                  Edit Prompt
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ReleaseIcon />}
                  onClick={handleRelease}
                >
                  Release
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Progress Bar (when running) */}
      {isRunning && testRun && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body1">
              Running {testRun.total_press_releases} extractions in parallel...
            </Typography>
          </Box>
          <LinearProgress
            variant="indeterminate"
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Paper>
      )}

      {/* No test run yet or failed */}
      {canStartNewTest && !starting && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {isFailed ? 'Test Failed' : 'Ready to Test'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isFailed
              ? 'The previous test run failed. You can retry.'
              : 'This will run your prompt against 5 test press releases and show the results.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleStartTest}
          >
            {isFailed ? 'Retry Test Run' : 'Start Test Run'}
          </Button>
        </Paper>
      )}

      {/* Test Results */}
      {testRun && testRun.extractions && testRun.extractions.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {testRun.extractions.map((extraction: TestExtractionRun) => (
            <Card key={extraction.id} variant="outlined">
              <CardHeader
                avatar={getStatusIcon(extraction.status)}
                title={extraction.case_name || `Press release ${extraction.press_release_id.slice(0, 8)}...`}
                subheader={
                  extraction.status === 'success'
                    ? `${formatLatency(extraction.latency_ms)} | ${formatTokens(extraction.tokens_used)} tokens`
                    : extraction.status
                }
                action={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      href={`/schemes/evaluate/${extraction.press_release_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      title="Open in Evaluation"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => toggleExtraction(extraction.id)}>
                      {expandedExtractions.includes(extraction.id)
                        ? <ExpandLessIcon />
                        : <ExpandMoreIcon />
                      }
                    </IconButton>
                  </Box>
                }
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => toggleExtraction(extraction.id)}
              />
              <Collapse in={expandedExtractions.includes(extraction.id)}>
                <CardContent>
                  <>
                    {extraction.status === 'failed' && extraction.error_message && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {extraction.error_message}
                      </Alert>
                    )}

                    {/* Press Release Input */}
                    <Typography variant="subtitle2" gutterBottom>
                      Press Release Input
                    </Typography>
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {extraction.title}
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflow: 'auto',
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          '& p': { mb: 1.5, mt: 0 },
                          '& p:last-child': { mb: 0 },
                        }}
                        dangerouslySetInnerHTML={{ __html: extraction.body_text || '' }}
                      />
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                      Extraction Output
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        backgroundColor: 'grey.50',
                        p: 2,
                        borderRadius: 1,
                        maxHeight: 400,
                        overflow: 'auto',
                        m: 0,
                      }}
                    >
                      {formatOutput(extraction.output)}
                    </Box>

                    {/* Chain of Thought */}
                    {extraction.response_metadata?.reasoning && extraction.response_metadata.reasoning.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <ChainOfThought steps={extraction.response_metadata.reasoning} />
                      </Box>
                    )}

                    {/* Web Searches Section */}
                    {extraction.response_metadata?.web_search_used && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <SearchIcon fontSize="small" color="info" />
                          <Typography variant="subtitle2">
                            Web Search {extraction.response_metadata.web_searches?.length
                              ? `(${extraction.response_metadata.web_searches.length} queries)`
                              : '(used)'}
                          </Typography>
                        </Box>
                        {extraction.response_metadata.web_searches && extraction.response_metadata.web_searches.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {extraction.response_metadata.web_searches.map((search, idx) => (
                              <Chip
                                key={idx}
                                label={search.query}
                                size="small"
                                variant="outlined"
                                color={search.status === 'completed' ? 'success' : 'default'}
                                sx={{ justifyContent: 'flex-start', width: 'fit-content', maxWidth: '100%' }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Model used web search to gather information
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Citations Section */}
                    {extraction.response_metadata?.citations && extraction.response_metadata.citations.length > 0 && (() => {
                      // Deduplicate citations by URL for display
                      const uniqueCitations = extraction.response_metadata.citations.filter(
                        (citation, idx, arr) => arr.findIndex(c => c.url === citation.url) === idx
                      );
                      return (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LinkIcon fontSize="small" color="secondary" />
                            <Typography variant="subtitle2">
                              Citations ({uniqueCitations.length})
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {uniqueCitations.map((citation, idx) => (
                              <Typography
                                key={idx}
                                variant="body2"
                                component="a"
                                href={citation.url || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {citation.title || citation.url || 'Unknown source'}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}

                    {/* Usage Stats */}
                    {extraction.response_metadata?.usage && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Input: {formatTokens(extraction.response_metadata.usage.input_tokens)} tokens
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Output: {formatTokens(extraction.response_metadata.usage.output_tokens)} tokens
                        </Typography>
                      </Box>
                    )}
                  </>
                </CardContent>
              </Collapse>
            </Card>
          ))}
        </Box>
      )}

    </Layout>
  );
}
